// ============================================================
// operationRegistry — 操作台帳（オペレータ業務の自動化レベル宣言）
//
// 正: booking-platform/md/ai-control-center.md §15
//
// システム上でオペレータができる操作を全部ここに登録し、AI がどこまで
// 実行できるかを **コードで宣言** する。昇格（B→A）は tier の1文字を変えるだけ
// （reconcile の fields.ts editable と同じ思想）。
//
//   tier A: AI が自動実行してよい（読み取り・可逆・実績で昇格したもの）
//   tier B: 確認カードで人の承認を得てから実行する
//   tier C: AI からは実行できない。「その操作は◯◯で行います」と案内だけする
//
// 昇格基準（§11-2 準拠）: 50実行・良評価80%以上・重大事故ゼロ。
// 評価は agent_execution_logs の result_status を暗黙評価として使う
// （success=採用 / cancelled=不採用）。レビュー: /ai_agent_log。
// ============================================================

export type OperationTier = 'A' | 'B' | 'C';

export interface OperationEntry {
    /** tools.ts の functionDeclarations の name。tier C は実ツールが無くてもよい */
    tool: string;
    tier: OperationTier;
    /** 人向けの短い説明（tier C は案内文の材料になる） */
    label: string;
    /** tier C のとき: 代わりにどこで操作するか */
    guide?: string;
}

export const OPERATION_REGISTRY: OperationEntry[] = [
    // ---- 読み取り（tier A: 自動実行可） ----
    { tool: 'search_reservations',    tier: 'A', label: '予約の検索・詳細表示' },
    { tool: 'search_vehicle_types',   tier: 'A', label: '車両タイプ・在庫の検索' },
    { tool: 'list_all_vehicle_types', tier: 'A', label: '車両タイプ一覧' },
    { tool: 'calculate_price',        tier: 'A', label: '料金計算・見積もり' },
    { tool: 'search_customer',        tier: 'A', label: '顧客検索（過去の予約履歴）' },
    { tool: 'get_vehicle_notion',     tier: 'A', label: '車両情報（Notion）の照会' },
    { tool: 'get_accident_notion',    tier: 'A', label: '事故・修理情報の照会' },
    { tool: 'search_notion',          tier: 'A', label: 'Notion 検索' },
    { tool: 'get_notion_page',        tier: 'A', label: 'Notion ページ取得' },
    // 2026-10-31: サイドパネルの Square 欄と同じ取り方（search_by_contact.php・Bearer）。読むだけ
    { tool: 'get_square_payment_status', tier: 'A', label: 'Square の決済状況（請求書の状態・金額・決済の試み・返金・明細・補償の支払い判定）' },

    // ---- 変更の下ごしらえ（記録#25・読み取り専用の事前チェック） ----
    { tool: 'check_schedule_conflict', tier: 'A', label: '日時変更の衝突チェック（車両の重なり・同タイプの空き）' },
    { tool: 'preview_price_change',    tier: 'A', label: '料金再計算プレビュー（自社=料金計算API / OTA=日割り・補償は車種単価×日数）' },
    { tool: 'list_available_vehicles', tier: 'A', label: '期間内に空いている号車の一覧（ガチャ枠は複数タイプのプールから）' },

    // ---- セッション（S3・切替と履歴照会は可逆） ----
    { tool: 'open_reservation_session', tier: 'A', label: '予約セッションへの切替（1予約=1セッション）' },
    { tool: 'get_reservation_timeline', tier: 'A', label: '予約の作業履歴タイムライン（4系統マージ・読み取り専用）' },

    // ---- 手順カード（2026-10-30。表示とスタッフの承認だけ・データは変えない） ----
    { tool: 'propose_plan',            tier: 'A', label: '複数の手順がある依頼の手順一覧を示して承認を受ける（手順カード）' },
    { tool: 'advance_plan',            tier: 'A', label: '手順カードのステップに完了・スキップ・スタッフ待ちの印を付ける' },

    // ---- 記録（追記のみ・外向きの副作用なし） ----
    { tool: 'record_conversation',     tier: 'A', label: 'やりとりの記録（改善のための会話ログ保存）' },
    // 社内スタッフ宛ての通知のみ（お客様には送らない）。矛盾を黙らないための即時連絡なので確認カードは挟まない
    { tool: 'report_contradiction',    tier: 'A', label: '会話内の矛盾をスタッフ全員へ即時メール（エスカレーション）' },
    // 以後すべての会話に影響するので確認カードを挟む
    { tool: 'remember_rule',           tier: 'B', label: '教わった業務ルールの記憶（今後の全会話に反映）' },

    // ---- メール（起案は可逆なので A、送信は取り消せないので B） ----
    { tool: 'list_mail_templates',     tier: 'A', label: 'メールテンプレート一覧（サイドパネルと同じ定型）' },
    { tool: 'archive_mail',            tier: 'A', label: '受信メールのアーカイブ（可逆・mail-queue の処理済み化）' },
    { tool: 'list_reservation_mails',  tier: 'A', label: '予約に紐づく受信メール一覧（mail_id の解決・読み取り専用）' },
    { tool: 'get_mail_content',        tier: 'A', label: '受信メール本文の読み取り' },
    { tool: 'set_mail_followup',       tier: 'A', label: 'フォローアップ期限の設定（スヌーズ相当・可逆・期限到来時に返信なしなら再浮上）' },
    { tool: 'check_mail_attachments',  tier: 'A', label: 'メール添付の AI 判定（免許証・証明書等の読み取り。判定は補助・最終確認は人）' },
    { tool: 'draft_reservation_email', tier: 'A', label: 'お客様宛メールの起案（送信はしない・テンプレ/言語指定可）' },

    // ---- 書き込み（tier B: 確認カード必須） ----
    { tool: 'send_reservation_email',    tier: 'B', label: 'お客様宛メールの送信（宛先はサーバが予約から決定）' },
    { tool: 'update_reservation',        tier: 'B', label: '予約の変更（ステータス・日時・連絡先・メモ）' },
    { tool: 'create_reservation',        tier: 'B', label: '新規予約の作成' },
    { tool: 'cancel_reservation_full',   tier: 'B', label: 'キャンセル一式（ステータス + Square請求書取消 + メモ）' },
    // 2026-10-31 前田さん: 失敗した請求書の再発行をエージェントで。直前に Square を読み直し、支払いのある請求書は止める
    { tool: 'void_square_invoice',       tier: 'B', label: '古い Square 請求書の片付け（未払い=Square で取消／FAILED・取消済み=予約から外す。メモ付き）' },
    { tool: 'create_square_invoice',     tier: 'B', label: '新しい Square 請求書の作成と予約への結び付け（サイドパネルと同じ経路。送るかは確認カードで）' },
    { tool: 'create_accident_notion',    tier: 'B', label: '事故報告の作成' },
    { tool: 'send_operator_request',     tier: 'B', label: '出発・返却のリクエストウィンドウ送信' },
    { tool: 'set_reservation_checks',    tier: 'B', label: '確認・同意チェックの変更（免許/支払い/規約/免責/同意。監査メモ付き）' },
    // 2026-09-01 前田さん指示で C→B 昇格。同タイプ内のみ・衝突チェック内蔵・実績経路保存の3防壁つき
    { tool: 'assign_vehicle',            tier: 'B', label: '車両アサインの変更（同タイプ内／ガチャ枠はプール内・変更先の重なりチェック内蔵）' },
    // 2026-10-03 前田さん「計算は AI・結果は人がチェック」。金額は計算根拠つきカードで承認後に保存・保存後検証
    { tool: 'apply_price_change',        tier: 'B', label: '料金の再計算と反映（補償の追加・削除／日程変更。カードで各金額と根拠を確認）' },
    { tool: 'set_license_checked',       tier: 'B', label: '（旧名・互換）免許証確認 → set_reservation_checks へ' },

    // ---- AI からは実行しない（tier C: 案内のみ） ----
    { tool: 'change_price', tier: 'C', label: '料金の変更',
      guide: '任意の金額への手動変更は予約サイドパネルで行います。補償の追加・削除や日程変更に伴う再計算は apply_price_change（確認カード）で反映できます' },
    { tool: 'change_vehicle_type', tier: 'C', label: '車両タイプをまたぐ変更',
      guide: '車両タイプの変更は車種・料金が変わるため予約サイドパネルで行います（同タイプ内の号車変更、およびガチャ枠のプール内での変更は assign_vehicle で AI が実行できます）' },
    { tool: 'merge_customer', tier: 'C', label: '顧客の統合',
      guide: '顧客の統合は /customers の統合候補タブで行います（別人を結合する事故を防ぐため人が判断します）' },
    { tool: 'carmore_approve', tier: 'C', label: 'carmore の予約承認',
      guide: 'carmore は自社システムでは完結しません。carmore の管理画面で承認してください' },
];

/** tier C の操作一覧をシステムプロンプト用の文章にする */
export function buildTierCGuide(): string {
    const rows = OPERATION_REGISTRY.filter((o) => o.tier === 'C');
    if (!rows.length) return '';
    return [
        '# AI が実行してはいけない操作（依頼されたら下記の案内だけを返す）',
        ...rows.map((o) => `- ${o.label}: ${o.guide}`),
    ].join('\n');
}

export function tierOf(tool: string): OperationTier | undefined {
    return OPERATION_REGISTRY.find((o) => o.tool === tool)?.tier;
}
